import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { contactUsQuery } from './queries'
import { FormData, FormErrors } from '@/types/contact'
import { validateEmail, validateMessage, validateName } from '@/utils/contact'

export const useContactForm = (onSuccess?: () => void) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [rating, setRating] = useState<number>(5)
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false)

  const [formData, setFormData] = useState<FormData>({
    category: 'error',
    email: '',
    name: '',
    message: ''
  })

  const [errors, setErrors] = useState<FormErrors>({})

  const resetForm = () => {
    setFormData({ category: 'error', email: '', name: '', message: '' })
    setRating(5)
    setIsConsentChecked(false)
    setTurnstileToken(null)
    setErrors({})
  }

  const mutation = useMutation({
    mutationFn: (payload: any) => contactUsQuery(payload),
    onSuccess: () => {
      setTimeout(() => {
        toast.info('Raven Sent!', {
          description: 'Your message has been received. Thank you!'
        })
        resetForm()
        onSuccess?.()
      }, 250)
    },
    onError: error => {
      toast.error('Failed to send message.', {
        description:
          error.message || 'An unexpected error occurred. Please try again.'
      })
    },
    onSettled: () => {
      setIsLoading(false)
    }
  })

  const handleInputChange = (field: keyof FormData, value: string): void => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field as keyof FormErrors]: undefined
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    if (!validateEmail(formData.email))
      newErrors.email = 'Please provide a valid email address.'
    const nameError = validateName(formData.name)
    if (nameError) newErrors.name = nameError
    const messageError = validateMessage(formData.message)
    if (messageError) newErrors.message = messageError
    if (!turnstileToken)
      newErrors.turnstile = 'Please complete the security verification.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    if (!validateForm()) return

    setIsLoading(true)
    let data: { rating?: string; consent?: string } = {}
    if (formData.category === 'testimonial') {
      data = {
        rating: `${rating}`,
        consent: isConsentChecked ? 'true' : 'false'
      }
    }

    const payload = {
      ...formData,
      token: turnstileToken!,
      data
    }
    mutation.mutate(payload)
  }

  const handleTurnstileChange = (
    token: string | null,
    error?: 'error' | 'expired'
  ) => {
    setTurnstileToken(token)
    if (token) {
      setErrors(prev => ({ ...prev, turnstile: undefined }))
    } else if (error === 'error') {
      setErrors(prev => ({
        ...prev,
        turnstile: 'Security verification failed. Please try again.'
      }))
    } else if (error === 'expired') {
      setErrors(prev => ({
        ...prev,
        turnstile: 'Security verification expired. Please verify again.'
      }))
    }
  }

  const canSubmit = !isLoading && turnstileToken !== null

  return {
    isLoading,
    formData,
    errors,
    rating,
    isConsentChecked,
    canSubmit,
    setRating,
    setIsConsentChecked,
    handleInputChange,
    handleSubmit,
    handleTurnstileChange
  }
}
